import { useState } from 'react';
import { api, uploadToSignedUrl } from '../../core/api/client';
import { SelfieCamera } from './SelfieCamera';
import { reportError } from '../../core/telemetry/report';
import { t } from '../../platform/lang';

/**
 * Registering a driver's face, once, on their own phone.
 *
 * Called a SCAN throughout, not a photo. That is not dressing it up: the image
 * is measured against the approved baseline by face recognition, so scanning is
 * literally what happens. The owner asked for the stronger word because the
 * deterrent only works if drivers understand the app is checking their face —
 * someone who thinks it merely snaps a picture is far likelier to try handing
 * the phone over. The small print still says plainly that a photo is taken.
 *
 * Why not the portrait the office already holds: those were taken years apart
 * on different cameras in different light. A dim car-park selfie compared
 * against one of those produces mismatches that mean nothing, and a review
 * queue full of meaningless flags is a control that gets ignored within a week.
 *
 * The photo is NOT used until the office approves it. That is deliberate — a
 * baseline the driver could set and reset at will would authenticate nobody,
 * because whoever took the latest one becomes the reference.
 *
 * Enrollment is NOT optional and cannot be postponed — see the note at the
 * bottom of this file. The one way past it is a camera that genuinely will not
 * open, which is recorded as a fault; medicine moving is worth more than a
 * photograph, but a driver choosing to have no face on file is not a thing this
 * app allows.
 */

type Phase = 'intro' | 'camera' | 'saving' | 'done';

export function FaceReference({ onFinished }: { onFinished: () => void }) {
  const [phase, setPhase] = useState<Phase>('intro');
  const [error, setError] = useState<string | null>(null);
  // Set only when the camera genuinely refuses to open. See the note below.
  const [cameraStuck, setCameraStuck] = useState(false);

  const send = async (blob: Blob) => {
    setPhase('saving');
    try {
      const signed = await api.signIdentity(blob.type || 'image/jpeg');
      await uploadToSignedUrl(signed.uploadUrl, blob, signed.requiredHeaders);
      await api.setFaceReference(signed.objectPath);
      setPhase('done');
    } catch {
      setError(t('faceSendFail'));
      setPhase('intro');
    }
  };

  if (phase === 'saving') {
    return <main className="screen"><h1>{t('faceSending')}</h1>
      <p className="hint">One moment.</p></main>;
  }

  if (phase === 'done') {
    // "Saved", not "all set". The scan may still turn out to be unusable, and
    // telling a driver they are finished when they might be asked again loses
    // their patience the second time. Deliberately says nothing about who looks
    // at it — see the note on faceSmall in platform/lang.ts.
    return (
      <main className="screen">
        <h1>{t('faceSentTitle')}</h1>
        <p className="hint">{t('faceSentBody')}</p>
        <p className="muted small">{t('faceSentSmall')}</p>
        <button className="btn primary big" onClick={onFinished}>{t('continue')}</button>
      </main>
    );
  }

  if (phase === 'camera') {
    return (
      <main className="screen">
        <h1>{t('faceTitle')}</h1>
        <SelfieCamera
          hint={t('faceCamHint')}
          shutterLabel={t('faceShutter')}
          onCapture={(b) => void send(b)}
          onUnavailable={(m) => {
            // Recorded as a fault, so a phone that can never enrol shows up as
            // a broken camera rather than as a driver who never got round to it.
            reportError('camera_failed', 'enrollment', m);
            setCameraStuck(true);
            setError(m);
            setPhase('intro');
          }}
        />
        <button className="btn link" onClick={() => setPhase('intro')}>{t('faceBack')}</button>
      </main>
    );
  }

  return (
    <main className="screen">
      <h1>{t('faceTitle')}</h1>
      <p className="hint">{t('faceIntro')}</p>

      <div className="banner">
        <strong>{t('faceBefore')}</strong>
        <ul className="plain-list">
          <li>{t('faceRule1')}</li>
          <li>{t('faceRule2')}</li>
          <li>{t('faceRule3')}</li>
          <li>{t('faceRule4')}</li>
        </ul>
      </div>

      <p className="muted small">{t('faceCapNote')}</p>
      <p className="muted small">{t('faceSmall')}</p>

      {error && <div className="banner warn-banner">{error}</div>}

      <button className="btn primary big" onClick={() => setPhase('camera')}>
        {t('faceStart')}
      </button>

      {/*
        * There is NO "Not now" any more.
        *
        * There was, and it was the reason one driver worked two days with
        * no face on file: the enrollment screen offered to go away, so it went
        * away, every time. A baseline that can be postponed indefinitely is not
        * a baseline, and every later check against it comes back
        * 'not_compared' — which is to say, the whole feature was optional.
        *
        * The escape below appears ONLY after the camera has actually failed to
        * open, and it is recorded as a fault. Nobody can choose it.
        */}
      {cameraStuck && (
        <button className="btn link" onClick={onFinished}>{t('idCameraStuck')}</button>
      )}
    </main>
  );
}
